import RegularHeader from '../../components/Common/RegularHeader';
import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Alert from '../../components/Alert';
import Loader from '../../components/Loader';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import feather from 'feather-icons';
import BottomNavbar from '../../components/Home/BottomNavbar';
import DayWiseLineChart from '../../components/Charts/DayWiseLineChart';
import serverClient from '../../api/ServerClient';
import { DeleteToken } from '../../auth/Auth';
import {
    amountDisplay,
    getMonthName,
    getUserTimezone,
    normalizeMonthParam,
} from '../../util/helper';
import BackButton from '../../components/BackButton';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useLanguage } from '../../context/LanguageContext';
import { getAccountImage } from '../../util/transactionHelper';
import { getPageMeta } from '../../util/seoHelper';

const getPercentage = (totalBalance, accountBalance) => {
    return (accountBalance * 100) / totalBalance;
};

const AccountBox = ({ account, totalBalance, language, t }) => (
    <div className="col-6">
        <Link to={`/account/${account.id}`} style={{ textDecoration: 'none' }}>
            <div className="saving-plan-box">
                <div className="account-box-header">
                    <div className="account-icon-wrapper">
                        <img src={getAccountImage(account.id)} alt={account.name} loading="lazy" />
                    </div>
                    <h4 style={{ margin: 0 }}>{account?.name}</h4>
                </div>

                <h3 className="pt-1">{t(account?.type)}</h3>

                <div
                    className="progress mt-2"
                    role="progressbar"
                    aria-label="progressbar"
                    aria-valuenow={getPercentage(totalBalance, account?.month_end_balance)}
                    aria-valuemin="0"
                    aria-valuemax="100"
                >
                    <div
                        className={`progress-bar bg-success`}
                        style={{
                            width: `${getPercentage(totalBalance, account?.month_end_balance)}%`,
                        }}
                    ></div>
                </div>

                <h6 className={account?.month_end_balance < 0 ? 'mt-2 text-danger' : 'mt-2'}>
                    {t('balance')}: <b>{amountDisplay(account?.month_end_balance, language)}</b>
                </h6>
            </div>
        </Link>
    </div>
);

// Empty State Component
const EmptyAccountState = ({ t }) => (
    <div className="empty-accounts-state text-center mt-5">
        <div className="empty-state-content">
            <i className="empty-icon" data-feather="briefcase"></i>
            <h3 className="empty-title">{t('noAccounts')}</h3>
            <p className="empty-description">{t('noAccountBalanceData')}</p>
        </div>
    </div>
);

const AccountListSection = ({ accounts, totalBalance, language, t }) => (
    <section className="section-b-space">
        <div className="custom-container">
            {accounts && accounts.length > 0 ? (
                <div className="row gy-3">
                    {accounts.map((account) => (
                        <AccountBox
                            key={account.id}
                            account={account}
                            totalBalance={totalBalance}
                            language={language}
                            t={t}
                        />
                    ))}
                </div>
            ) : (
                <EmptyAccountState t={t} />
            )}
        </div>
    </section>
);

function ReportBalance() {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    const timeZone = getUserTimezone();
    const { language, t } = useLanguage();

    const navigate = useNavigate();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [statisticsData, setStatisticsData] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [customMonth, setCustomMonth] = useState('');

    const [searchParams, setSearchParams] = useSearchParams();
    const month = searchParams.get('month');
    const [dataMonth, setDataMonth] = useState(month);

    const fetchStatisticsData = useCallback(async () => {
        try {
            setLoading(true);
            let uri;
            if (dataMonth) {
                uri = `/api/user/report/balance?month=${normalizeMonthParam(dataMonth)}`;
            } else {
                uri = '/api/user/report/balance';
            }
            const response = await serverClient.get(`${uri}`);
            setStatisticsData(response?.data?.data);
            setAccounts(response?.data?.data?.singleMonthBalance);
        } catch (e) {
            if (e.response?.status === 401) {
                DeleteToken();
                setError(t('userNotAuthorized'));
                navigate('/login');
            } else {
                setError(t('somethingWentWrong'));
            }
        } finally {
            setLoading(false);
        }
    }, [dataMonth, t, navigate]);

    useEffect(() => {
        fetchStatisticsData();
        feather.replace();
    }, [fetchStatisticsData]);

    useEffect(() => {
        feather.replace();
    });

    if (loading || !statisticsData) {
        return (
            <>
                <RegularHeader headerTitle={t('balanceReport')} />
                <div className="custom-container p-4">
                    {error && <Alert alert={'danger'}>{error}</Alert>}

                    <Loader loading={loading} />
                    <BottomNavbar />
                </div>
            </>
        );
    }
    const currentMonth = dayjs().tz(getUserTimezone()).format('MMM');
    let monthTitle = '';
    if (month !== null) {
        monthTitle = dayjs(month).endOf('month').format('D MMM');
    } else {
        monthTitle = t('today');
    }

    const handlePrevMonth = (e) => {
        e.preventDefault();
        const startOfLastMonth = dayjs()
            .tz(timeZone)
            .subtract(1, 'month')
            .startOf('month')
            .format('YYYY-MM-DD');
        setDataMonth(startOfLastMonth);
        setSearchParams({ month: startOfLastMonth });
    };

    const handleThisMonth = () => {
        setDataMonth(null);
        setSearchParams({});
    };

    const handleApplyCustomMonth = () => {
        const start = dayjs(customMonth).tz(timeZone).startOf('day').format('YYYY-MM-DD');
        setDataMonth(start);
        setSearchParams({ month: start });

        const modalEl = document.getElementById('period');
        const modalInstance = window.bootstrap.Modal.getInstance(modalEl);
        modalInstance?.hide();
    };

    return (
        <>
            <Helmet>
                <title>{getPageMeta('reportBalance', language).title}</title>
                <meta
                    name="description"
                    content={getPageMeta('reportBalance', language).description}
                />
                <meta name="keywords" content={getPageMeta('reportBalance', language).keywords} />
                <link rel="canonical" href={getPageMeta('reportBalance', language).url} />
                {getPageMeta('reportBalance', language).noindex && (
                    <meta name="robots" content="noindex, follow" />
                )}

                {/* Open Graph Tags */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={getPageMeta('reportBalance', language).title} />
                <meta
                    property="og:description"
                    content={getPageMeta('reportBalance', language).description}
                />
                <meta property="og:url" content={getPageMeta('reportBalance', language).url} />
                <meta property="og:image" content={getPageMeta('reportBalance', language).image} />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={getPageMeta('reportBalance', language).title} />
                <meta
                    name="twitter:description"
                    content={getPageMeta('reportBalance', language).description}
                />
                <meta name="twitter:image" content={getPageMeta('reportBalance', language).image} />
            </Helmet>
            {/*<RegularHeader headerTitle={"Balance Report"}/>*/}

            <div className="modal add-money-modal fade" id="period" tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2 className="modal-title">{t('selectPeriod')}</h2>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label htmlFor="inputfromdate" className="form-label">
                                    {t('fromDate')}
                                </label>
                                <input
                                    type="month"
                                    className="form-control"
                                    id="inputfromdate"
                                    value={customMonth}
                                    onChange={(e) => setCustomMonth(e.target.value)}
                                />
                            </div>

                            <button
                                className="btn theme-btn successfully w-100 mt-4"
                                onClick={handleApplyCustomMonth}
                            >
                                {t('apply')}
                            </button>
                        </div>

                        <button type="button" className="btn close-btn" data-bs-dismiss="modal">
                            <i className="icon" data-feather="x"></i>
                        </button>
                    </div>
                </div>
            </div>

            <header className="section-t-space">
                <div className="custom-container">
                    <div className="header-panel">
                        <BackButton fallback="/reports" />
                        <h2>{t('balanceReport')}</h2>

                        <div className="dropdown">
                            <Link
                                to="#"
                                className="back-btn"
                                role="button"
                                data-bs-toggle="dropdown"
                            >
                                <i className="icon" data-feather="settings"></i>
                            </Link>

                            <ul className="dropdown-menu">
                                <li>
                                    <Link className="dropdown-item" to="" onClick={handleThisMonth}>
                                        {t('thisMonth')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="#"
                                        onClick={handlePrevMonth}
                                        className="dropdown-item"
                                    >
                                        {t('lastMonth')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        className="dropdown-item"
                                        to="#period"
                                        data-bs-toggle="modal"
                                    >
                                        {t('custom')}
                                    </Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </header>

            <section>
                <div className="custom-container">
                    {error && <Alert alert={'danger'}>{error}</Alert>}
                    <Loader loading={loading} />

                    <div className="statistics-banner">
                        <div className="d-flex justify-content-center gap-3">
                            <div className="statistics-content">
                                <h5>
                                    {t('totalBalance')} | {monthTitle}
                                </h5>
                                <h3 className="text-white fw-semibold">
                                    {'৳ '}
                                    {amountDisplay(statisticsData.totalBalance, language)}
                                </h3>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <DayWiseLineChart
                expenseData={statisticsData?.balanceDateWise ?? []}
                daysInMonth={statisticsData?.daysInMonth ?? 0}
                header={`${t('totalBalance')} | ${getMonthName(currentMonth, t)}`}
                title={t('balance')}
            />

            <div className="custom-container">
                <section>
                    <div className="title">
                        <h2>
                            {t('accountBalances')} | {monthTitle}
                        </h2>
                    </div>
                </section>
            </div>

            {accounts && (
                <AccountListSection
                    accounts={accounts}
                    totalBalance={statisticsData?.totalBalance}
                    language={language}
                    t={t}
                />
            )}

            <BottomNavbar />
        </>
    );
}

export default ReportBalance;
